import React, { useState, useEffect } from 'react';

// Utils
import { editorStateToJsonString, isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useUpdateAiSetting } from 'Shared/utils/hooks/use-update-ai-setting';
import { useUpdateAiReport } from 'Shared/utils/hooks/use-update-ai-report';
import { useUpdateAiTemplateSection } from 'Shared/utils/hooks/use-update-ai-template-section';

// Types
import { UpdateAiTemplateSectionType, UpdateAiTemplateTypeType } from 'Shared/types/UpdateAiTemplate.type';

// Components
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpRichTextInput, EditorState } from 'Wpds/components/rich-text';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import Spinner from 'Shared/components/Spinner';

// Styles
import './ReportSection.scss';

type Props = {
  section: UpdateAiTemplateSectionType;
  updateAiSettingSlug: string;
  handleGenerateReportClick: Function;
};

const ReportSection = ({ section, updateAiSettingSlug, handleGenerateReportClick }: Props) => {
  const { report, template } = useUpdateAiSetting({ slug: updateAiSettingSlug });

  const { currentlyGeneratingSlugs, handleUpdateAiReport, handleCancelGenerateReport } = useUpdateAiReport({
    slug: report.slug,
  });

  const { handleUpdateTemplateWithDebounce } = useUpdateAiTemplateSection({
    updateAiSettingSlug: updateAiSettingSlug,
    section,
  });

  const isGenerating = currentlyGeneratingSlugs.includes(section.slug);
  const bodyItem = report.body.find(i => i.templateSectionSlug === section.slug);

  //   if (!bodyItem) return null;
  const [isReportActive, setIsReportActive] = useState(false);
  const [body, setBody] = useState(null);
  const [isTitleInputActive, toggleIsTitleInputActive] = useToggle(false);
  const [title, setTitle] = useState('');

  const hasError = bodyItem?.error;
  const progressMessage = bodyItem?.progressMessage;
  // const body = Array.isArray(bodyItem?.sectionBody) ? bodyItem?.sectionBody?.join('\r\n') : bodyItem?.sectionBody;

  const handleSetBody = value => {
    setBody(Array.isArray(value) ? value?.join('\r\n') : value);
  };

  const handleRetryClick = () => {
    handleGenerateReportClick({ templateSectionSlugs: [section.slug] });
  };

  const handleUpdateSectionBody = (value: EditorState) => {
    handleSetBody(value);
    handleUpdateAiReport({
      body: [{ template_section_slug: section.slug, section_body: editorStateToJsonString(value) }],
    });
  };

  const handleUpdateTitle = (value: string) => {
    setTitle(value);
    handleUpdateTemplateWithDebounce({ data: { title: value } });
  };

  const handleCancelClick = () => {
    handleCancelGenerateReport({ templateSectionSlugs: [section.slug] });
  };

  useEffect(() => {
    if (!isTitleInputActive) setTitle(section.title);
  }, [section, isTitleInputActive]);

  useEffect(() => {
    if (isReportActive === false && isEmpty(body) && !isGenerating) handleSetBody(bodyItem?.sectionBody);
  }, [bodyItem, body, isGenerating]);

  useEffect(() => {
    if (isGenerating) setBody(null);
  }, [isGenerating]);

  const baseClassName = 'ReportSection';
  const salesTemplate = template.templateType === UpdateAiTemplateTypeType.SALES;
  return (
    <div>
      <div className={`${baseClassName}__title`}>
        <WpTextField
          label="Title"
          id="title"
          value={title}
          placeholder="Add a title"
          onChange={handleUpdateTitle}
          hideBorder
          labelHidden
          max={50}
          onFocus={toggleIsTitleInputActive}
          onBlur={toggleIsTitleInputActive}
          readOnly={isGenerating}
        />
      </div>
      {isGenerating ? (
        <div className={`${baseClassName}__loading`}>
          {progressMessage ? (
            <>
              {progressMessage.split('\n').map(progressMessageLine => (
                <div>{progressMessageLine}</div>
              ))}

              {salesTemplate && <Spinner activeIf />}
              <WpButton variant="tertiary" onClick={handleCancelClick}>
                Cancel
              </WpButton>
            </>
          ) : (
            <>
              <LoadingShimmer shape="rectangle" width="65%" height="16px" />
              <LoadingShimmer shape="rectangle" width="60%" height="16px" />
              <LoadingShimmer shape="rectangle" width="55%" height="16px" />
            </>
          )}
        </div>
      ) : (
        <>
          {hasError ? (
            <EmptyStateContainer
              subTitle="An error occurred generating update."
              grayBackground
              inContainer
              button={
                <WpButton variant="tertiary" onClick={handleRetryClick}>
                  <WpIcon name="redo" />
                  Re-try
                </WpButton>
              }
            />
          ) : (
            <WpRichTextInput
              options={{ showBorder: false }}
              value={body}
              onChange={handleUpdateSectionBody}
              height="auto"
              onFocus={() => setIsReportActive(true)}
              onBlur={() => setIsReportActive(false)}
            />
          )}
        </>
      )}
    </div>
  );
};
export default ReportSection;
